import React, { useCallback, useState, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { Popup } from 'devextreme-react/popup';
import { Form, Label, FormRef, GroupItem, SimpleItem, ButtonItem, EmptyItem, RequiredRule, CustomRule } from 'devextreme-react/form';
import { ButtonType } from 'devextreme-react/common';
import 'devextreme-react/text-area';
import 'devextreme/ui/date_range_box';

import LabelTemplate from '../label-template/LabelTemplate';

import CustomerApiCredentialApi from '../../api/CustomerApiCredentialApi';

import PersonalAccessTokenCreateDto from '../../classes/dtos/credentialmodule/PersonalAccessTokenCreateDto';


interface PathCredentialCreateFormProps {
    Created(): void;
}

export interface PathCredentialCreateFormHandle {
    Open: () => void;
}

const personalAccessTokenCreateDto = new PersonalAccessTokenCreateDto(null, new Date(), null);

const PathCredentialCreateForm = forwardRef<PathCredentialCreateFormHandle, PathCredentialCreateFormProps>(({ Created }, ref) => {

    const formRef = useRef<FormRef>(null);

    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
        Open() {
            if (formRef.current !== null) {
                const formInstance = formRef.current.instance();
                formInstance.clear();
            }
            setIsOpen(true);
        }
    }));

    const fieldEditorOptions = {
        valueChangeEvent: 'keyup'
    };

    const closeDialog = () => {
        setIsOpen(false);
    };

    const popupAttributes = useMemo(() => {
        return {
            id: 'elementId',
            class: 'class-name-popup'
        }
    }, []);

    const handleSubmit = useCallback(async (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        let createResult = await CustomerApiCredentialApi.CreateCustomerApiCredentialsAsync(personalAccessTokenCreateDto.ValidityStart!, personalAccessTokenCreateDto.ValidityEnd, personalAccessTokenCreateDto.Name ?? "");
        if (createResult.IsFailed()) {
            alert(createResult.ErrorMessage);
            return;
        }
        Created();
        setIsOpen(false);
    }, []);

    const createButtonOptions = {
        text: 'Create',
        type: 'default' as ButtonType,
        useSubmitBehavior: true,
        width: '100%',
        icon: 'add'
    };

    const validateStartDate = ({ value }) => {
        const [startDate] = value || [null];
        return startDate !== null && startDate !== undefined;
    };

    return (
        <React.Fragment>
            <Popup
                wrapperAttr={popupAttributes}
                visible={isOpen}
                onHiding={closeDialog}
                dragEnabled={false}
                hideOnOutsideClick={false}
                showCloseButton={true}
                showTitle={true}
                title="Let's create a new personal access token !"
                container=".dx-viewport"
                width={600}
                height={420}
            >
                <form onSubmit={handleSubmit}>
                    <div style={{ width: 480, color: '#9393A2', fontSize: 16, fontFamily: 'Manrope', fontWeight: '500', lineHeight: '24px', letterSpacing: 0.32, wordWrap: 'break-word' }}>To go further, please enter a name and a validity period.</div>
                    <Form
                        ref={formRef}
                        formData={personalAccessTokenCreateDto}
                        showColonAfterLabel={true}
                        showValidationSummary={false}
                        validationGroup="PersonalAccessTokenCreateDtoData"
                    >
                        <GroupItem>
                            <SimpleItem dataField="Name" editorType="dxTextBox" editorOptions={fieldEditorOptions}>
                                <Label render={LabelTemplate('rename')} />
                                <RequiredRule message="Name is required." />
                            </SimpleItem>
                            <SimpleItem dataField="ValidityRange" editorType="dxDateRangeBox" editorOptions={{
                                ...fieldEditorOptions,
                                onValueChanged: (e) => {
                                    const [start, end] = e.value;
                                    personalAccessTokenCreateDto.ValidityStart = start;
                                    personalAccessTokenCreateDto.ValidityEnd = end;
                                }
                            }}>
                                <Label text="Validity period" />
                                <CustomRule
                                    message="Start of validity is required."
                                    validationCallback={validateStartDate}
                                />
                            </SimpleItem>
                        </GroupItem>
                        <EmptyItem colSpan={2} />
                        <GroupItem>
                            <ButtonItem buttonOptions={createButtonOptions} />
                        </GroupItem>
                    </Form>
                </form>
            </Popup>
        </React.Fragment>
    );
});

export default PathCredentialCreateForm;